import { uiText } from '../utils/i18n';
import { useState, useEffect, useCallback } from 'react';
import { useStore } from '../store';
import { useT } from '../utils/i18n';
import { formatDateTime } from '../utils/format-time';
import s from './MeetingsPage.module.css';

interface Meeting {
  id: string;
  title: string;
  zoomMeetingId: number | null;
  joinUrl: string | null;
  startUrl: string | null;
  password: string | null;
  hostUserId: string;
  hostName: string;
  scheduledAt: number | null;
  durationMinutes: number;
  status: 'scheduled' | 'live' | 'ended' | 'cancelled';
  participants: string[];
  minutesStatus: 'none' | 'transcribing' | 'generating' | 'done' | 'error';
  hasMinutes: boolean;
  hasTranscript: boolean;
  createdAt: number;
  updatedAt: number;
}

interface MeetingDetail extends Meeting {
  minutesText?: string;
  transcript?: string;
}

type ViewState = { mode: 'list' } | { mode: 'detail'; meetingId: string } | { mode: 'create' };

const FILTERS = ['all', 'scheduled', 'ended', 'has_minutes'] as const;
type Filter = (typeof FILTERS)[number];

const filterLabels: Record<Filter, string> = {
  all: '全部',
  scheduled: '进行中/待开',
  ended: '已结束',
  has_minutes: '有纪要',
};

function statusLabel(st: string): string {
  switch (st) {
    case 'scheduled': return uiText("待开始");
    case 'live': return uiText("进行中");
    case 'ended': return uiText("已结束");
    case 'cancelled': return uiText("已取消");
    default: return st;
  }
}

function statusColor(st: string): string {
  switch (st) {
    case 'scheduled': return 'var(--olive, #5a6a2a)';
    case 'live': return '#c54a2f';
    case 'ended': return 'var(--ink-dim, #888)';
    default: return '#888';
  }
}

function minutesStatusLabel(ms: string): string {
  switch (ms) {
    case 'transcribing': return uiText("转写中");
    case 'generating': return uiText("生成纪要中");
    case 'done': return uiText("已生成纪要");
    case 'error': return uiText("生成失败");
    default: return '';
  }
}

export function MeetingsPage() {
  const token = useStore((st) => st.token);
  const t = useT();

  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<ViewState>({ mode: 'list' });
  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');
  const [detail, setDetail] = useState<MeetingDetail | null>(null);

  const fetchMeetings = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch('/api/meetings', { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const data = await res.json(); setMeetings(data.meetings || []); }
    } catch { /* ignore */ }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchMeetings(); }, [fetchMeetings]);

  const fetchDetail = useCallback(async (id: string) => {
    if (!token) return;
    try {
      const [mRes, minRes] = await Promise.all([
        fetch(`/api/meetings/${id}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`/api/meetings/${id}/minutes`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (mRes.ok) {
        const m = await mRes.json();
        let md: { minutesText?: string; transcript?: string } = {};
        if (minRes.ok) md = await minRes.json();
        setDetail({ ...m, minutesText: md.minutesText, transcript: md.transcript });
      }
    } catch { /* ignore */ }
  }, [token]);

  const filtered = meetings.filter((m) => {
    if (filter === 'scheduled') return m.status === 'scheduled' || m.status === 'live';
    if (filter === 'ended') return m.status === 'ended';
    if (filter === 'has_minutes') return m.hasMinutes;
    return true;
  }).filter((m) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return m.title.toLowerCase().includes(q) || m.hostName.toLowerCase().includes(q);
  });

  const totalHours = Math.round(meetings.filter((m) => m.status === 'ended').reduce((sum, m) => sum + m.durationMinutes, 0) / 60 * 10) / 10;
  const minutesCount = meetings.filter((m) => m.hasMinutes).length;

  const handleCreate = async (title: string, duration: number, scheduledAt?: string) => {
    if (!token) return;
    try {
      const res = await fetch('/api/meetings', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ title, duration, scheduledAt }),
      });
      if (res.ok) {
        const data = await res.json();
        await fetchMeetings();
        setView({ mode: 'list' });
        if (data.joinUrl) window.open(data.joinUrl, '_blank');
      }
    } catch { /* ignore */ }
  };

  const handleDelete = async (id: string) => {
    if (!token || !confirm(uiText("确定删除这个会议？"))) return;
    try {
      await fetch(`/api/meetings/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      await fetchMeetings();
      if (view.mode === 'detail') setView({ mode: 'list' });
    } catch { /* ignore */ }
  };

  const handleGenerateMinutes = async (id: string) => {
    if (!token) return;
    try {
      await fetch(`/api/meetings/${id}/generate-minutes`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      setTimeout(() => fetchDetail(id), 2000);
    } catch { /* ignore */ }
  };

  // ── Detail / Create views ──
  if (view.mode === 'detail' || view.mode === 'create') {
    return (
      <div className={s.container}>
        <div className={s.header}>
          <div className={s.headerLeft}>
            <span className={s.eyebrow}>meeting room</span>
            <h1 className={s.title}>{t('nav.meetings')}</h1>
          </div>
        </div>
        <div className={s.body}>
          {view.mode === 'create' ? (
            <CreateMeetingView onBack={() => setView({ mode: 'list' })} onCreate={handleCreate} />
          ) : (
            <MeetingDetailView
              meetingId={view.meetingId}
              detail={detail}
              onBack={() => { setView({ mode: 'list' }); setDetail(null); }}
              onLoad={fetchDetail}
              onDelete={handleDelete}
              onGenerateMinutes={handleGenerateMinutes}
            />
          )}
        </div>
      </div>
    );
  }

  // ── List view (two-column) ──
  return (
    <div className={s.container}>
      {/* Header */}
      <div className={s.header}>
        <div className={s.headerLeft}>
          <span className={s.eyebrow}>meeting room</span>
          <h1 className={s.title}>{t('nav.meetings')}</h1>
        </div>
        <div className={s.headerSpacer} />
        <span className={s.headerMeta}>
          {meetings.length} {uiText("会议 ·")} {totalHours}h · {minutesCount} {uiText("纪要")}
        </span>
      </div>

      {/* Two-column body */}
      <div className={s.body}>
        <div className={s.grid}>
          {/* LEFT — action rail */}
          <div className={s.rail}>
            {/* Create meeting hero */}
            <div className={s.createHero} onClick={() => setView({ mode: 'create' })}>
              <div className={s.heroIconBox}>
                <svg width={22} height={22} viewBox="0 0 24 24" fill="none">
                  <rect x={3} y={5} width={14} height={11} rx={2} stroke="currentColor" strokeWidth={2} />
                  <path d="M17 9l4-2v10l-4-2" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <div className={s.heroTitle}>{t('meetings.create')}</div>
              <div className={s.heroSub}>{t('meetings.createHint')}</div>
            </div>

            {/* Join meeting */}
            <JoinMeetingCard />

            {/* Upload recording */}
            <UploadRecordingCard token={token} onRefresh={fetchMeetings} />

            {/* Stats */}
            <div className={s.statsStrip}>
              <span className={s.statsNum}>{meetings.length}</span>
              <div>
                <div className={s.statsLabel}>{t('meetings.totalMeetings')}</div>
                <div className={s.statsSub}>
                  {totalHours} {t('meetings.hours')} · {minutesCount} {t('meetings.minutesCount')}
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT — search + meeting cards */}
          <div>
            {/* Toolbar */}
            <div className={s.toolbar}>
              <div className={s.searchBox}>
                <svg width={14} height={14} viewBox="0 0 24 24" fill="none" style={{ opacity: 0.4, flexShrink: 0 }}>
                  <circle cx={11} cy={11} r={7} stroke="currentColor" strokeWidth={2} />
                  <path d="M16 16l5 5" stroke="currentColor" strokeWidth={2} strokeLinecap="round" />
                </svg>
                <input className={s.searchInput} placeholder={t('meetings.searchPlaceholder')} value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
              <div className={s.filterGroup}>
                {FILTERS.map((f) => (
                  <button key={f} className={`${s.filterBtn} ${filter === f ? s.filterBtnActive : ''}`} onClick={() => setFilter(f)}>
                    {uiText(filterLabels[f])}
                  </button>
                ))}
              </div>
            </div>

            {/* Section header */}
            <div className={s.sectionHeader}>
              <span className={s.sectionLabel}>{t('meetings.records')}</span>
              <span className={s.sectionCount}>· {filtered.length}</span>
              <span className={s.sectionLine} />
            </div>

            {/* Cards */}
            {loading ? (
              <div className={s.emptyState}><div className={s.emptyTitle}>{t('meetings.loading')}</div></div>
            ) : filtered.length === 0 ? (
              <div className={s.emptyState}>
                <div className={s.emptyTitle}>{t('meetings.empty')}</div>
                <div className={s.emptyHint}>{t('meetings.emptyHint')}</div>
              </div>
            ) : (
              <div className={s.cardsGrid}>
                {filtered.map((m) => (
                  <MeetingCard key={m.id} meeting={m} onClick={() => { setView({ mode: 'detail', meetingId: m.id }); fetchDetail(m.id); }} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────────

function MeetingCard({ meeting: m, onClick }: { meeting: Meeting; onClick: () => void }) {
  const isGroup = m.participants.length > 2;
  const msLabel = minutesStatusLabel(m.minutesStatus);
  const msColor = m.minutesStatus === 'done' ? 'var(--olive, #5a6a2a)' : m.minutesStatus === 'error' ? '#c54a2f' : 'var(--butter, #c79a3a)';

  return (
    <div className={s.meetingCard} onClick={onClick}>
      <div className={s.cardTop}>
        <div className={s.cardIcon} style={{ background: isGroup ? 'var(--sky-soft, #c2d6e8)' : 'var(--olive-soft, #d4dcae)' }}>
          {isGroup ? (
            <svg width={16} height={16} viewBox="0 0 24 24" fill="none">
              <circle cx={8} cy={8} r={3} fill="currentColor" /><circle cx={16} cy={8} r={3} fill="currentColor" />
              <path d="M4 18c0-2.5 2-4.5 4.5-4.5h7c2.5 0 4.5 2 4.5 4.5" fill="currentColor" />
            </svg>
          ) : (
            <svg width={16} height={16} viewBox="0 0 24 24" fill="none">
              <circle cx={9} cy={8} r={3.5} fill="currentColor" /><circle cx={16} cy={9} r={2.5} fill="currentColor" />
              <path d="M3 19c0-3 2.5-5 6-5s6 2 6 5" fill="currentColor" />
            </svg>
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0, paddingTop: 1 }}>
          <div className={s.cardTitleRow}><span className={s.cardTitle}>{m.title}</span></div>
          <div className={s.cardMeta}>
            <span className={s.cardTypeBadge} style={{ background: isGroup ? 'var(--sky-soft, #c2d6e8)' : 'var(--olive-soft, #d4dcae)' }}>
              {isGroup ? uiText("多人") : uiText("双人")}
            </span>
            <span className={s.cardTime}>{formatDateTime(m.scheduledAt || m.createdAt)} · {m.durationMinutes} min</span>
          </div>
        </div>
      </div>
      {m.hostName && <div className={s.cardPreview}>{uiText("主持人：")}{m.hostName}{m.participants.length > 0 ? ` · ${m.participants.length} 位参会者` : ''}</div>}
      <div className={s.cardFooter}>
        <span className={s.cardStatus}><span className={s.statusDot} style={{ background: statusColor(m.status) }} />{statusLabel(m.status)}</span>
        {msLabel && <span className={s.cardStatus}><span className={s.statusDot} style={{ background: msColor }} />{msLabel}</span>}
        <span className={s.cardAction}>{uiText("查看")} <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }}>→</span></span>
      </div>
    </div>
  );
}

function JoinMeetingCard() {
  const [showModal, setShowModal] = useState(false);
  const [url, setUrl] = useState('');

  const handleJoin = () => {
    if (!url.trim()) return;
    window.open(url.startsWith('http') ? url : `https://zoom.us/j/${url}`, '_blank');
    setUrl('');
    setShowModal(false);
  };

  return (
    <>
      <div className={s.actionCard} onClick={() => setShowModal(true)}>
        <div className={s.actionIconBox} style={{ background: 'var(--olive-soft, #d4dcae)' }}>
          <svg width={14} height={14} viewBox="0 0 24 24" fill="none">
            <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" stroke="var(--olive, #5a6a2a)" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <div style={{ flex: 1 }}>
          <div className={s.actionTitle}>{uiText("加入会议")}</div>
          <div className={s.actionSub}>{uiText("用链接或 ID 进入")}</div>
        </div>
      </div>

      {showModal && (
        <div className={s.modalOverlay} onClick={() => setShowModal(false)}>
          <div className={s.modalBox} onClick={(e) => e.stopPropagation()}>
            <div className={s.modalHeader}>
              <span className={s.modalTitle}>{uiText("加入会议")}</span>
              <button className={s.modalClose} onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <div className={s.modalBody}>
              <label className={s.formLabel}>{uiText("会议链接或 ID")}</label>
              <input
                className={s.joinInput}
                placeholder={uiText("粘贴 Zoom 链接或输入会议 ID")}
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                autoFocus
                onKeyDown={(e) => { if (e.key === 'Enter') handleJoin(); if (e.key === 'Escape') setShowModal(false); }}
              />
            </div>
            <div className={s.modalFooter}>
              <button className={s.btnSecondary} onClick={() => setShowModal(false)}>{uiText("取消")}</button>
              <button className={s.btnPrimary} disabled={!url.trim()} onClick={handleJoin}>{uiText("加入会议")}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function UploadRecordingCard({ token, onRefresh }: { token: string | null; onRefresh: () => void }) {
  const handleUpload = async () => {
    const input = document.createElement('input');
    input.type = 'file'; input.accept = '.txt,.srt,.vtt,.json';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file || !token) return;
      const text = await file.text();
      try {
        const res = await fetch('/api/meetings', { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ title: file.name.replace(/\.[^.]+$/, '') }) });
        if (res.ok) {
          const { id } = await res.json();
          await fetch(`/api/meetings/${id}/upload-transcript`, { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ transcript: text }) });
          onRefresh();
        }
      } catch { /* ignore */ }
    };
    input.click();
  };

  return (
    <div className={s.actionCard} onClick={handleUpload}>
      <div className={s.actionIconBox} style={{ background: 'var(--plum-soft, #d8ccef)' }}>
        <svg width={14} height={14} viewBox="0 0 24 24" fill="none">
          <path d="M12 3v12M5 10l7-7 7 7" stroke="var(--plum, #8a6ac8)" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />
          <path d="M20 21H4" stroke="var(--plum, #8a6ac8)" strokeWidth={2.5} strokeLinecap="round" />
        </svg>
      </div>
      <div style={{ flex: 1 }}>
        <div className={s.actionTitle}>{uiText("上传录音")}</div>
        <div className={s.actionSub}>{uiText("自动转写生成纪要")}</div>
      </div>
    </div>
  );
}

function CreateMeetingView({ onBack, onCreate }: { onBack: () => void; onCreate: (title: string, duration: number, scheduledAt?: string) => void }) {
  const [title, setTitle] = useState('');
  const [duration, setDuration] = useState(60);
  const [scheduledAt, setScheduledAt] = useState('');
  const [creating, setCreating] = useState(false);

  return (
    <>
      <button className={s.detailBack} onClick={onBack}>{uiText("← 返回")}</button>
      <div className={s.detailTitle}>{uiText("发起会议")}</div>
      <div style={{ maxWidth: 460 }}>
        <div className={s.formGroup}>
          <label className={s.formLabel}>{uiText("会议主题")}</label>
          <input className={s.formInput} placeholder={uiText("例：产品评审会、周例会...")} value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
        </div>
        <div className={s.formGroup}>
          <label className={s.formLabel}>{uiText("会议时长（分钟）")}</label>
          <input className={s.formInput} type="number" min={15} max={480} value={duration} onChange={(e) => setDuration(parseInt(e.target.value) || 60)} />
        </div>
        <div className={s.formGroup}>
          <label className={s.formLabel}>{uiText("预定时间（可选，留空则立即开始）")}</label>
          <input className={s.formInput} type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} />
        </div>
        <div className={s.formActions}>
          <button className={s.btnSecondary} onClick={onBack}>{uiText("取消")}</button>
          <button className={s.btnPrimary} disabled={!title.trim() || creating}
            onClick={async () => { if (!title.trim()) return; setCreating(true); await onCreate(title.trim(), duration, scheduledAt || undefined); setCreating(false); }}>
            {creating ? uiText("创建中...") : uiText("创建会议")}
          </button>
        </div>
      </div>
    </>
  );
}

function MeetingDetailView({ meetingId, detail, onBack, onLoad, onDelete, onGenerateMinutes }: {
  meetingId: string; detail: MeetingDetail | null; onBack: () => void; onLoad: (id: string) => void; onDelete: (id: string) => void; onGenerateMinutes: (id: string) => void;
}) {
  useEffect(() => { onLoad(meetingId); }, [meetingId, onLoad]);

  if (!detail) return (<><button className={s.detailBack} onClick={onBack}>{uiText("← 返回")}</button><div style={{ color: 'var(--ink-dim)', padding: 40, textAlign: 'center' }}>{uiText("加载中...")}</div></>);

  return (
    <>
      <button className={s.detailBack} onClick={onBack}>{uiText("← 返回列表")}</button>
      <div className={s.detailTitle}>{detail.title}</div>
      <div className={s.detailMeta}>
        <span className={s.detailMetaItem}><span className={s.statusDot} style={{ background: statusColor(detail.status), display: 'inline-block', width: 7, height: 7, borderRadius: '50%', marginRight: 5 }} />{statusLabel(detail.status)}</span>
        <span className={s.detailMetaItem}>{formatDateTime(detail.scheduledAt || detail.createdAt)}</span>
        <span className={s.detailMetaItem}>{detail.durationMinutes} {uiText("分钟")}</span>
        <span className={s.detailMetaItem}>{uiText("主持人:")} {detail.hostName}</span>
      </div>

      {detail.joinUrl && detail.status !== 'ended' && (
        <div className={s.detailSection}>
          <a href={detail.joinUrl} target="_blank" rel="noopener noreferrer" className={s.joinBtn} style={{ textDecoration: 'none' }}>
            <svg width={18} height={18} viewBox="0 0 24 24" fill="none"><rect x={3} y={5} width={14} height={11} rx={2} stroke="currentColor" strokeWidth={2} /><path d="M17 9l4-2v10l-4-2" stroke="currentColor" strokeWidth={2} strokeLinecap="round" /></svg>
            {uiText("加入 Zoom 会议")}
          </a>
          {detail.password && <div className={s.detailMetaItem} style={{ marginTop: 8 }}>{uiText("会议密码:")} {detail.password}</div>}
        </div>
      )}

      {detail.participants.length > 0 && (
        <div className={s.detailSection}>
          <div className={s.detailSectionTitle}>{uiText("参会者")}</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {detail.participants.map((p, i) => <span key={i} className={s.cardTypeBadge} style={{ background: 'var(--bg-surface)' }}>{p}</span>)}
          </div>
        </div>
      )}

      <div className={s.detailSection}>
        <div className={s.detailSectionTitle}>
          {uiText("会议纪要")}
          {detail.minutesStatus === 'none' && detail.hasTranscript && <button className={s.btnPrimary} style={{ fontSize: 11, padding: '5px 12px' }} onClick={() => onGenerateMinutes(detail.id)}>{uiText("生成纪要")}</button>}
          {detail.minutesStatus === 'generating' && <span className={s.detailMetaItem} style={{ color: 'var(--butter, #c79a3a)' }}>{uiText("生成中...")}</span>}
        </div>
        {detail.minutesText ? <div className={s.minutesContent}>{detail.minutesText}</div> : <div style={{ color: 'var(--ink-dim)', fontSize: 12 }}>{detail.hasTranscript ? uiText("转写已完成，可生成纪要") : uiText("暂无纪要 — 会议结束后自动转写")}</div>}
      </div>

      {detail.transcript && (
        <div className={s.detailSection}>
          <div className={s.detailSectionTitle}>{uiText("原始转写")}</div>
          <details>
            <summary style={{ cursor: 'pointer', fontSize: 12, color: 'var(--ink-dim)' }}>{uiText("展开查看")}</summary>
            <div className={s.minutesContent} style={{ marginTop: 8, maxHeight: 300, overflowY: 'auto', fontSize: 12 }}>{detail.transcript}</div>
          </details>
        </div>
      )}

      <div style={{ marginTop: 20 }}><button className={s.btnSecondary} style={{ color: '#c54a2f' }} onClick={() => onDelete(detail.id)}>{uiText("删除会议")}</button></div>
    </>
  );
}
